<template>
  <div class="home-action-box">
    <h2>{{ title }}</h2>
    <div>
      <p class="subtext">{{ subtext }}</p>
      <ul>
        <li v-for="(item, i) in items" :key="`item-${i}`">
          <span>
            <img class="app-icon" :src="getImageUrl(item.icon)" :alt="item.name" />
          </span>
          <span>
            <div>{{ item.name }}</div>
            <div>{{ item.description }}</div>
            <div v-if="item.additional">{{ item.additional }}</div>
          </span>
        </li>
      </ul>
    </div>

    <button class="app-btn-large" @click="handleRouting(action.route)">
      {{ action.label }}
    </button>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

// Vuex store and route
const store = useStore();
const router = useRouter();

// Reactive data refs
const title = ref('Make a new reservation');
const subtext = ref('Items you may need');
const items = ref([]);
const action = ref({});

// Props
const props = defineProps({
  isNewReservation: {
    type: Boolean,
    required: false,
    default: true     
  }
});

// Map state and getters with computed
const LANG = computed(() => store.state.user.LANG);
const PAGES = computed(() => store.state.user.PAGES);

const getImageUrl = (icon) => {
  return require(`@ridmv/web-themes/images/icons/${icon}`);
};

// Watcher for changes
watch(() => props.isNewReservation, (isNew) => {
  if (isNew) {
    title.value = 'Make a new reservation';
    subtext.value = 'Items you may need';
    items.value = [
      {
        name: 'A current email address',
        description: 'If you\'d like us to send you a confirmation and remind you about your reservation.',
        icon: 'envelope-solid.svg'
      },
      {
        name: 'Your phone number',
        description: 'If you\'d like us to let you know if something changes about your reservation.',
        icon: 'phone-solid.svg'
      }
    ];
  } else {
    title.value = 'Modify or cancel a reservation';
    subtext.value = 'Items you may need';
    items.value = [
      {
        name: 'Your confirmation number, email address or phone number',
        description: 'So that we can look up your reservation.',
        additional: '',
        icon: 'check-square-solid.svg'
      }
    ];
  }
}, { immediate: true });

// Lifecycle hooks
onMounted(() => {
  if (props.isNewReservation) {
    action.value = {
      route: {
        name: PAGES.value.selectType,
      },
      label: LANG.value.btnMakeRes,
    };
  } else {
    action.value = {
      route: {
        name: PAGES.value.findRes,
      },
      label: LANG.value.btnUpdateExistingRes,
    };
  }
});

// Methods
const handleRouting = (route) => {
  store.dispatch('user/resetReservationDetails', true);
  router.push(route);
};
</script>


<style scoped lang="scss">
.home-action-box {
  background-color: var(--ri-darker-blue);
  border-radius: var(--border-rounding);
  box-shadow: 0 0 5px var(--ri-blue);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin: 1em min(1rem, 1.5vw);
  padding: 2em 3em;
  width: clamp(400px, 38vw, 90vw);
  button {
    margin-top: auto;
    width: 100%;
  }
  ul {
    margin-bottom: 1em;
    li {
      display: flex;
      flex-wrap: nowrap;
      align-items: flex-start;
      justify-content: flex-start;
      padding-bottom: 1.5em;
      span:first-child {    // icon
        padding: 5px 1.25em 0 0;
      }
      span:nth-child(2) {   // list content
        div {
          line-height: 1.5em;
        }
        div:first-child {
          @extend %font-sans-bold;
          font-size: 1.125rem;
        }
        div:nth-child(2) {
          @extend %font-sans-regular;
        }
        div:nth-child(3) {
          color: var(--ri-orange);
          padding-top: 0.5em;
        }
      }
    }
  }
  .subtext {
    color: var(--ri-orange);
    @extend %font-sans-bold;
    font-size: 1.125rem;
    line-height: 3em;
    text-transform: uppercase;
  }
}
</style>
